import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import Select, { components as SelectComponents } from "react-select";
import { Save, CheckCircle, CheckCircleOutline, AddCircleOutline, Close, Undo, Edit, Schedule, HourglassEmpty } from "@mui/icons-material";
import Sidebarmenu from "../../Sidebar/Sidebarmenu";
import Header from "../Header/Header";
import { toast } from "react-toastify";
import loadergif from "../../../img/landing/loader123.gif";
import {
  getThalilist,
  getAllcaterer,
  getDishName,
  createCalender,
  Updatethali,
  getCatererSchedule,
  createCatererSchedule,
  updateCatererSchedule,
} from "../../../Redux/Reducer/AuthReducer";
import { HIJRI_MONTH_NAMES, hijriMonthRange, getCurrentHijriYearMonth, getHijriParts } from "../../HijriCalendar/getHijriDate";

// Requested column order for dish-category cells; any dish_type not in this
// list (e.g. Mithas) still renders, just appended after these in alpha order,
// so nothing silently disappears from the grid.
const CATEGORY_ORDER = ["Gravy", "Kharas", "Tarkari", "Rice", "Roti", "Fruit", "Salad", "Sherbet"];
const NIYAAZ_TYPE_BY_SCHEDULE = { FMBTHALI: 0, MIQAAT: 1 };

// Caterer Grid's Lead Caterer/Team Member/status workflow now lives inside
// the Daily Menu Grid's own Caterer column - hidden for now rather than
// removed, since the view/data/thunks are all still intact underneath.
const SHOW_CATERER_GRID_TOGGLE = false;

// Status is never picked directly by the user - it's derived on save
// (Pending for a caterer-only row, Confirmed once dishes are added too -
// see handleSaveRow) or set by the caterer via the fmb_mobile app.
const CATERER_STATUS_LABELS = { PENDING: "Pending", TENTATIVE: "Tentative", CONFIRMED: "Confirmed" };

// Same name-keyword pattern as UserRegistrationSearch.js - the dish
// master's own allow_skip_rice/allow_skip_roti attribute isn't reliably
// set, so allow-skip on save is derived from the dish name instead.
const isRice = (n) => n.includes("rice") || n.includes("chawal") || n.includes("khichdi");
const isRoti = (n) =>
  n.includes("roti") ||
  n.includes("naan") ||
  n.includes("tacos") ||
  n.includes("paratha") ||
  n.includes("manda") ||
  n.includes("bread");

function parseMenu(menuStr) {
  if (!menuStr) return [];
  try {
    const parsed = JSON.parse(menuStr);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Shows a small dish-type label (e.g. "Gravy") right-aligned inside the
// select's value area, before its clear/dropdown indicators - so the user
// can tell which column's dropdown they're in even while it's still on its
// placeholder. Passed the category via the custom `dishTypeLabel` prop
// (read back off selectProps, per react-select's components API).
function DishTypeValueContainer({ children, ...props }) {
  return (
    <SelectComponents.ValueContainer {...props}>
      {children}
      {!props.hasValue && props.selectProps.dishTypeLabel && (
        <span className="mp-select-dish-type-badge">{props.selectProps.dishTypeLabel}</span>
      )}
    </SelectComponents.ValueContainer>
  );
}

export default function MenuPlanning() {
  const dispatch = useDispatch();
  const access_id = localStorage.getItem("access_id");

  const [loading, setLoading] = useState(true);
  const [scheduleList, setScheduleList] = useState([]);
  const [dishMaster, setDishMaster] = useState([]);
  const [catererMaster, setCatererMaster] = useState([]);
  const [catererScheduleList, setCatererScheduleList] = useState([]); // thaali_schedule_caterer rows
  const [viewMode, setViewMode] = useState("grid"); // "stats" | "grid" | "caterer"
  const [gridEdits, setGridEdits] = useState({}); // rowKey -> { dishSelections: {category: option[]}, leadCaterer, teamMember1, teamMember2, scheduleType, notes, notes2 }
  const [savingRows, setSavingRows] = useState({}); // rowKey -> true while a save is in flight
  const [extraRows, setExtraRows] = useState({}); // dateKey -> rowKey[] of user-added blank rows (via "+" next to Date)
  const [catererGridEdits, setCatererGridEdits] = useState({}); // rowKey -> { scheduleType, leadCaterer, teamMember1/2/3, notes }
  const [savingCatererRows, setSavingCatererRows] = useState({});
  const [extraCatererRows, setExtraCatererRows] = useState({}); // dateKey -> rowKey[]
  const [teamMemberSlotCounts, setTeamMemberSlotCounts] = useState({}); // Daily Menu Grid rowKey -> 0-2 visible Team Member slots
  const [unlockedCatererRows, setUnlockedCatererRows] = useState({}); // Daily Menu Grid rowKey -> true once unlocked via the edit icon
  const [dishSearchValue, setDishSearchValue] = useState(null); // global "which type/niyaaz is this dish?" lookup
  const [pastDaysCollapsed, setPastDaysCollapsed] = useState(true); // Daily Menu Grid: hide rows before today, on by default
  const [statsRangeMode, setStatsRangeMode] = useState("monthly"); // Statistics view: "monthly" | "quarterly"
  const nextLocalRowIdRef = useRef(0);
  const nextLocalCatererRowIdRef = useRef(0);
  const gridScrollRef = useRef(null); // the actual scrollable table container
  const gridScrollThumbRef = useRef(null); // custom always-visible scrollbar thumb
  const gridScrollBarRef = useRef(null); // the fixed-to-viewport track wrapper

  const initialHijri = useMemo(() => getCurrentHijriYearMonth(), []);
  const [hijriYear, setHijriYear] = useState(initialHijri.year);
  const [hijriMonth, setHijriMonth] = useState(initialHijri.month);

  // "Today" only exists within the Hijri month the page loaded on - the
  // Hide/Show Past Days toggle is meaningless (and hidden) on any other month.
  const isCurrentMonth = hijriYear === initialHijri.year && hijriMonth === initialHijri.month;

  // Re-fetch the schedule list only (dish/caterer masters don't change while
  // planning) - used on mount and again after every Save Draft.
  const refetchSchedule = () => {
    return dispatch(getThalilist()).then((res) => {
      if (res?.payload?.status === true) {
        setScheduleList(res.payload.data || []);
      } else {
        toast.error(res?.payload?.message || "Failed to load schedule data");
      }
    });
  };

  // Fetched regardless of which view is active - the Daily Menu Grid needs
  // to know about caterer-schedule assignments too (to prefill/lock its own
  // Caterer column), not just the Caterer Grid view itself.
  const refetchCatererSchedule = () => {
    return dispatch(getCatererSchedule()).then((res) => {
      if (res?.payload?.status === true) {
        setCatererScheduleList(res.payload.data || []);
      } else {
        toast.error(res?.payload?.message || "Failed to load caterer schedule data");
      }
    });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(getThalilist()),
      dispatch(getAllcaterer()),
      dispatch(getDishName(0)), // FMBTHALI dishes
      dispatch(getDishName(1)), // Niyaaz dishes
      dispatch(getCatererSchedule()),
    ])
      .then(([scheduleRes, catererRes, fmbDishRes, niyaazDishRes, catererScheduleRes]) => {
        if (scheduleRes?.payload?.status === true) {
          setScheduleList(scheduleRes.payload.data || []);
        } else {
          toast.error(scheduleRes?.payload?.message || "Failed to load schedule data");
        }
        if (catererRes?.payload?.status === true) {
          setCatererMaster(catererRes.payload.data || []);
        }
        if (catererScheduleRes?.payload?.status === true) {
          setCatererScheduleList(catererScheduleRes.payload.data || []);
        }
        // Tag each dish with which list it came from (0 = FMB Thaali, 1 = Miqaat/Niyaaz)
        // so dropdowns can be filtered per-row by that row's schedule type.
        const fmbDishes = (fmbDishRes?.payload?.status === true ? fmbDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 0,
        }));
        const niyaazDishes = (niyaazDishRes?.payload?.status === true ? niyaazDishRes.payload.data || [] : []).map((d) => ({
          ...d,
          _niyaazType: 1,
        }));
        setDishMaster([...fmbDishes, ...niyaazDishes]);
      })
      .catch(() => toast.error("Failed to load menu planning data"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edits are per-month scratch state - drop them when the visible month changes.
  // Also reset the past-days toggle back to its default (hidden) - the button
  // itself is only shown on the current month anyway, so this mainly matters
  // when navigating back to it.
  useEffect(() => {
    setGridEdits({});
    setExtraRows({});
    setCatererGridEdits({});
    setExtraCatererRows({});
    setTeamMemberSlotCounts({});
    setUnlockedCatererRows({});
    setPastDaysCollapsed(true);
  }, [hijriYear, hijriMonth]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => hijriMonthRange(hijriYear, hijriMonth),
    [hijriYear, hijriMonth]
  );

  // Statistics view range: a single Hijri month, or a rolling 3-month
  // ("quarterly") window anchored at hijriYear/hijriMonth when that toggle
  // is on. The Daily Menu Grid always stays single-month (rangeStart/rangeEnd
  // above) regardless of this.
  const { start: statsRangeStart, end: statsRangeEnd } = useMemo(() => {
    if (statsRangeMode !== "quarterly") return { start: rangeStart, end: rangeEnd };
    const quarterEndMonth = (hijriMonth + 2) % 12;
    const quarterEndYear = hijriYear + Math.floor((hijriMonth + 2) / 12);
    const { end } = hijriMonthRange(quarterEndYear, quarterEndMonth);
    return { start: rangeStart, end };
  }, [statsRangeMode, rangeStart, rangeEnd, hijriYear, hijriMonth]);

  function filterEntriesByRange(list, start, end) {
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime();
    return list.filter((item) => {
      if (!item?.schedule_day) return false;
      const t = new Date(item.schedule_day).getTime();
      return !Number.isNaN(t) && t >= startTime && t <= endTime;
    });
  }

  // Single Hijri month - drives the Daily Menu Grid (always one month at a time).
  const monthEntries = useMemo(
    () => filterEntriesByRange(scheduleList, rangeStart, rangeEnd),
    [scheduleList, rangeStart, rangeEnd]
  );

  // Statistics view range - same as monthEntries unless quarterly mode is on.
  const statsEntries = useMemo(
    () => filterEntriesByRange(scheduleList, statsRangeStart, statsRangeEnd),
    [scheduleList, statsRangeStart, statsRangeEnd]
  );

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Dish/caterer occurrence counts, trailing from the date the user is
  // actually scheduling - "last 30 days" and "last 90 days" ending on (and
  // including) that day, not tied to whatever Hijri month/quarter happens to
  // be showing. Memoized per scheduleList version, then cached per dateKey
  // so multiple rows on the same day (or repeated renders) don't re-scan.
  const occurrenceCountsByDate = useMemo(() => {
    const cache = {};
    return (dateKey) => {
      if (cache[dateKey]) return cache[dateKey];
      const refTime = new Date(dateKey).getTime();
      const monthlyStart = refTime - 29 * DAY_MS;
      const quarterlyStart = refTime - 89 * DAY_MS;
      const dishMonthly = {};
      const dishQuarterly = {};
      const catererMonthly = {};
      const catererQuarterly = {};
      scheduleList.forEach((item) => {
        if (!item?.schedule_day) return;
        const t = new Date(item.schedule_day).getTime();
        if (Number.isNaN(t) || t > refTime || t < quarterlyStart) return;
        const inMonthly = t >= monthlyStart;
        parseMenu(item.menu).forEach((dishName) => {
          if (!dishName || dishName === "-") return;
          const key = dishName.trim().toLowerCase();
          dishQuarterly[key] = (dishQuarterly[key] || 0) + 1;
          if (inMonthly) dishMonthly[key] = (dishMonthly[key] || 0) + 1;
        });
        [item.cater_name1, item.cater_name2].forEach((name) => {
          if (!name || name === "null" || name === "NA") return;
          const key = name.trim().toLowerCase();
          catererQuarterly[key] = (catererQuarterly[key] || 0) + 1;
          if (inMonthly) catererMonthly[key] = (catererMonthly[key] || 0) + 1;
        });
      });
      const result = { dishMonthly, dishQuarterly, catererMonthly, catererQuarterly };
      cache[dateKey] = result;
      return result;
    };
  }, [scheduleList]);

  // dateKey defaults to today - used by the global dish search, which isn't
  // tied to any particular row/date.
  function getDishCounts(dishName, dateKey = todayKey) {
    const { dishMonthly, dishQuarterly } = occurrenceCountsByDate(dateKey);
    const key = dishName?.trim().toLowerCase();
    return {
      monthlyCount: (key && dishMonthly[key]) || 0,
      quarterlyCount: (key && dishQuarterly[key]) || 0,
    };
  }

  function getCatererCounts(catererName, dateKey = todayKey) {
    const { catererMonthly, catererQuarterly } = occurrenceCountsByDate(dateKey);
    const key = catererName?.trim().toLowerCase();
    return {
      monthlyCount: (key && catererMonthly[key]) || 0,
      quarterlyCount: (key && catererQuarterly[key]) || 0,
    };
  }

  const dishTypeByName = useMemo(() => {
    const map = {};
    dishMaster.forEach((d) => {
      if (d?.dish_name) {
        map[d.dish_name.trim().toLowerCase()] = d.dish_type || "Other";
      }
    });
    return map;
  }, [dishMaster]);

  const summary = useMemo(() => {
    let thaaliEntries = 0;
    let miqaatEntries = 0;
    const uniqueDays = new Set();
    statsEntries.forEach((item) => {
      uniqueDays.add(item.schedule_day);
      if (item.schedule_type === "MIQAAT") miqaatEntries++;
      else thaaliEntries++;
    });
    return { uniqueDays: uniqueDays.size, thaaliEntries, miqaatEntries };
  }, [statsEntries]);

  const itemFrequency = useMemo(() => {
    const byCategory = {};
    statsEntries.forEach((item) => {
      const dishes = parseMenu(item.menu);
      const day = item.schedule_day ? new Date(item.schedule_day) : null;
      dishes.forEach((dishName) => {
        if (!dishName || dishName === "-") return;
        const category = dishTypeByName[dishName.trim().toLowerCase()] || "Other";
        if (!byCategory[category]) byCategory[category] = {};
        if (!byCategory[category][dishName]) byCategory[category][dishName] = { count: 0, last: null };
        const entry = byCategory[category][dishName];
        entry.count += 1;
        if (day && !Number.isNaN(day.getTime()) && (!entry.last || day > entry.last)) {
          entry.last = day;
        }
      });
    });
    // Same ordering as the Daily Menu Grid's columns (see `categories` below),
    // with "Other" forced last regardless of where it'd otherwise alphabetize.
    const orderIndex = (name) => {
      if (name === "Other") return CATEGORY_ORDER.length + 1;
      const idx = CATEGORY_ORDER.indexOf(name);
      return idx === -1 ? CATEGORY_ORDER.length : idx;
    };
    return Object.keys(byCategory)
      .sort((a, b) => {
        const diff = orderIndex(a) - orderIndex(b);
        return diff !== 0 ? diff : a.localeCompare(b);
      })
      .map((category) => ({
        category,
        dishes: Object.entries(byCategory[category])
          .map(([name, stat]) => ({ name, ...stat }))
          .sort((a, b) => b.count - a.count),
      }));
  }, [statsEntries, dishTypeByName]);

  const catererFrequency = useMemo(() => {
    const counts = {};
    statsEntries.forEach((item) => {
      const day = item.schedule_day ? new Date(item.schedule_day) : null;
      [item.cater_name1, item.cater_name2].forEach((name) => {
        if (!name || name === "null" || name === "NA") return;
        if (!counts[name]) counts[name] = { count: 0, last: null };
        counts[name].count += 1;
        if (day && !Number.isNaN(day.getTime()) && (!counts[name].last || day > counts[name].last)) {
          counts[name].last = day;
        }
      });
    });
    // Include zero-count caterers from the master list, matching the reference
    // spreadsheet's Caterer Frequency sheet (every caterer listed, even at 0).
    catererMaster.forEach((c) => {
      const name = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
      if (name && !counts[name]) {
        counts[name] = { count: 0, last: null };
      }
    });
    return Object.entries(counts)
      .map(([name, stat]) => ({ name, ...stat }))
      .sort((a, b) => b.count - a.count);
  }, [statsEntries, catererMaster]);

  // ---- Daily Menu Grid ----

  function toDateKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  const todayKey = useMemo(() => toDateKey(new Date()), []);

  // The grid's dropdowns live inside a horizontally-scrollable table
  // (overflow-x: auto), which clips the select menu popup unless it's
  // portaled straight to <body> - otherwise the option list is invisible/
  // unclickable on most rows.
  const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  const categories = useMemo(() => {
    const set = new Set();
    dishMaster.forEach((d) => {
      if (d?.dish_type) set.add(d.dish_type);
    });
    const orderIndex = (name) => {
      const idx = CATEGORY_ORDER.indexOf(name);
      return idx === -1 ? CATEGORY_ORDER.length : idx;
    };
    return Array.from(set).sort((a, b) => {
      const diff = orderIndex(a) - orderIndex(b);
      return diff !== 0 ? diff : a.localeCompare(b);
    });
  }, [dishMaster]);

  // Read-only lookup for the "which category/type is this dish under?" search
  // box - not wired to any grid cell, just helps the user find the right
  // column before picking the dish from that row's own dropdown.
  const dishSearchOptions = useMemo(() => {
    return dishMaster
      .filter((d) => d?.dish_name && d?.dish_type)
      .map((d) => ({
        value: d.id,
        label: d.dish_name,
        dishType: d.dish_type,
        niyaazType: d._niyaazType,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dishMaster]);

  const formatDishSearchOption = (option) => {
    const { monthlyCount, quarterlyCount } = getDishCounts(option.label);
    return (
      <div className="mp-dish-search-option">
        <div className="mp-dish-search-option-name">{option.label} - {option.dishType}</div>
        <div className="mp-dish-search-option-niyaaz">
          {option.niyaazType === 1 ? "Miqaat" : "FMB"}
          <span className="mp-dish-occurrence-counts">Monthly: {monthlyCount} &middot; Quarterly: {quarterlyCount}</span>
        </div>
      </div>
    );
  };

  // Dropdown-list-only label (formatOptionLabel's `context` param is "menu"
  // for options in the open list, "value" for the currently selected item) -
  // keeps the persisted selected-value display clean (just the dish name)
  // while the open list shows how often the dish has recently been used.
  // Shared by both the dish and caterer pickers below.
  const formatOptionLabelWithCounts = (option, { context }) => {
    if (context !== "menu") return option.label;
    return (
      <div className="mp-dish-pick-option">
        <span className="mp-dish-pick-option-name">{option.label}</span>
        <span className="mp-dish-occurrence-counts">Monthly: {option.monthlyCount || 0} &middot; Quarterly: {option.quarterlyCount || 0}</span>
      </div>
    );
  };

  // Keyed by niyaaz type (0 = FMB, 1 = Miqaat) so an existing entry's dish
  // names resolve to the dish from the correct list.
  const dishByNameAndType = useMemo(() => {
    const map = { 0: {}, 1: {} };
    dishMaster.forEach((d) => {
      if (d?.dish_name && (d._niyaazType === 0 || d._niyaazType === 1)) {
        map[d._niyaazType][d.dish_name.trim().toLowerCase()] = d;
      }
    });
    return map;
  }, [dishMaster]);

  const dishOptionsByCategoryAndType = useMemo(() => {
    const byType = { 0: {}, 1: {} };
    dishMaster.forEach((d) => {
      if (!d?.dish_type || (d._niyaazType !== 0 && d._niyaazType !== 1)) return;
      const byCategory = byType[d._niyaazType];
      if (!byCategory[d.dish_type]) byCategory[d.dish_type] = [];
      byCategory[d.dish_type].push({ value: d.id, label: d.dish_name });
    });
    Object.values(byType).forEach((byCategory) => {
      Object.keys(byCategory).forEach((c) => byCategory[c].sort((a, b) => a.label.localeCompare(b.label)));
    });
    return byType;
  }, [dishMaster]);

  // FMB rows only see FMB dishes, Miqaat rows only see Miqaat dishes.
  // Enriched with last-30/90-day occurrence counts (relative to the row's
  // own date) on every call rather than baked into the memo above, since
  // counts change far more often (any schedule edit, or just which row this
  // is) than the dish master list itself.
  const getDishOptions = (category, scheduleTypeValue, dateKey) => {
    const niyaazType = NIYAAZ_TYPE_BY_SCHEDULE[scheduleTypeValue] ?? 0;
    const options = dishOptionsByCategoryAndType[niyaazType]?.[category] || [];
    return options.map((option) => ({ ...option, ...getDishCounts(option.label, dateKey) }));
  };

  const catererOptions = useMemo(
    () =>
      catererMaster
        .map((c) => ({
          value: c.id,
          label: `${c?.first_name || ""} ${c?.last_name || ""}`.trim(),
        }))
        .filter((o) => o.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catererMaster]
  );

  // Enriched with last-30/90-day occurrence counts relative to the row's own
  // date, same rationale as getDishOptions above.
  const getCatererOptions = (dateKey) =>
    catererOptions.map((option) => ({ ...option, ...getCatererCounts(option.label, dateKey) }));

  const catererIdByName = useMemo(() => {
    const map = {};
    catererMaster.forEach((c) => {
      const name = `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
      if (name) map[name.trim().toLowerCase()] = c.id;
    });
    return map;
  }, [catererMaster]);

  const gridDays = useMemo(() => {
    const days = [];
    const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    const last = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
    while (cursor.getTime() <= last.getTime()) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [rangeStart, rangeEnd]);

  // "Hide Past Days" - drop rows before today so Today's row lands at the
  // top of the table. If the whole visible month is already in the past
  // (nothing left to show), fall back to the full list instead of hiding
  // every row.
  const visibleGridDays = useMemo(() => {
    if (!pastDaysCollapsed) return gridDays;
    const filtered = gridDays.filter((date) => toDateKey(date) >= todayKey);
    return filtered.length > 0 ? filtered : gridDays;
  }, [gridDays, pastDaysCollapsed, todayKey]);

  // Custom always-visible scrollbar for the Daily Menu Grid - the native
  // one is hidden via CSS since macOS's default overlay-scrollbar setting
  // makes it only appear during an active scroll gesture, which this whole
  // feature exists to fix. Thumb width/position is kept in sync with the
  // real scroll state imperatively (not via React state) so it doesn't
  // re-render the grid on every scroll tick.
  const updateScrollThumb = () => {
    const container = gridScrollRef.current;
    const thumb = gridScrollThumbRef.current;
    if (!container || !thumb) return;
    const { scrollWidth, clientWidth, scrollLeft } = container;
    if (scrollWidth <= clientWidth) {
      thumb.style.width = "100%";
      thumb.style.left = "0%";
      return;
    }
    const widthPct = (clientWidth / scrollWidth) * 100;
    const leftPct = (scrollLeft / (scrollWidth - clientWidth)) * (100 - widthPct);
    thumb.style.width = `${widthPct}%`;
    thumb.style.left = `${leftPct}%`;
  };

  // The scrollbar track is position:fixed to the bottom of the viewport (so
  // it stays visible while the page itself scrolls up/down), which means it
  // has to be told its left/width explicitly rather than inheriting them
  // from normal document flow - kept aligned with the real scrollable
  // table's own horizontal position/width (which shifts with the sidebar).
  const updateScrollBarPosition = () => {
    const container = gridScrollRef.current;
    const bar = gridScrollBarRef.current;
    if (!container || !bar) return;
    const rect = container.getBoundingClientRect();
    bar.style.left = `${rect.left}px`;
    bar.style.width = `${rect.width}px`;
  };

  // Every sticky column after the first (Save, fixed at left:0) needs its
  // `left` MEASURED off its actual predecessors' rendered widths rather than
  // assumed from CSS min-width - table-layout:auto only treats width/
  // min-width as advisory, so the browser can (and does) stretch a column
  // wider than declared when redistributing the table's own forced
  // min-width slack, which would otherwise throw the offset off. Which
  // classes are sticky, and in what order, differs per view: the Daily Menu
  // Grid freezes Date, Type, then Caterer (in that order); the Caterer Grid
  // keeps its original Type-then-Date order and doesn't freeze Caterer.
  const updateStickyColumnOffset = () => {
    const container = gridScrollRef.current;
    if (!container) return;
    const table = container.querySelector("table");
    if (!table) return;
    const isCatererGrid = table.classList.contains("mp-caterer-grid-table");
    const stickyClasses = isCatererGrid
      ? [".mp-grid-type-col", ".mp-grid-date-col"]
      : [".mp-grid-date-col", ".mp-grid-type-col", ".mp-grid-caterer-cell"];
    const saveIconCol = container.querySelector(".mp-grid-save-icon-col");
    const firstCol = container.querySelector(stickyClasses[0]);
    if (!saveIconCol || !firstCol) return;
    // The first sticky column after Save sits at a static CSS `left` (its
    // own fallback), so only columns from the second one onward need their
    // offset measured and set here.
    let offset = saveIconCol.getBoundingClientRect().width + firstCol.getBoundingClientRect().width;
    for (let i = 1; i < stickyClasses.length; i += 1) {
      const col = container.querySelector(stickyClasses[i]);
      if (!col) return;
      container.querySelectorAll(stickyClasses[i]).forEach((el) => {
        el.style.left = `${offset}px`;
      });
      offset += col.getBoundingClientRect().width;
    }
  };

  useEffect(() => {
    const container = gridScrollRef.current;
    if (!container) return undefined;
    updateScrollThumb();
    updateScrollBarPosition();
    updateStickyColumnOffset();
    container.addEventListener("scroll", updateScrollThumb);
    window.addEventListener("resize", updateScrollBarPosition);
    const resizeObserver = new ResizeObserver(() => {
      updateScrollThumb();
      updateScrollBarPosition();
      updateStickyColumnOffset();
    });
    resizeObserver.observe(container);
    return () => {
      container.removeEventListener("scroll", updateScrollThumb);
      window.removeEventListener("resize", updateScrollBarPosition);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, visibleGridDays, categories]);

  const handleScrollThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = gridScrollRef.current;
    if (!container) return;
    const track = e.currentTarget.parentElement;
    const trackRect = track.getBoundingClientRect();
    const startX = e.clientX;
    const startScrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    const handleMouseMove = (moveEvent) => {
      const deltaRatio = (moveEvent.clientX - startX) / trackRect.width;
      const deltaScroll = deltaRatio * container.scrollWidth;
      container.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + deltaScroll));
    };
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleScrollTrackMouseDown = (e) => {
    const container = gridScrollRef.current;
    if (!container || e.target === gridScrollThumbRef.current) return;
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickRatio = (e.clientX - trackRect.left) / trackRect.width;
    const maxScroll = container.scrollWidth - container.clientWidth;
    container.scrollLeft = Math.max(0, Math.min(maxScroll, clickRatio * container.scrollWidth - container.clientWidth / 2));
  };

  const SCHEDULE_TYPE_OPTIONS = [
    { value: "FMBTHALI", label: "FMB" },
    { value: "MIQAAT", label: "Miqaat" },
  ];

  // A day can now hold multiple schedule entries (e.g. two separate menus
  // for the same day) - group all of them per day instead of picking one.
  const entriesByDay = useMemo(() => {
    const map = {};
    monthEntries.forEach((item) => {
      if (!item?.schedule_day) return;
      const key = toDateKey(new Date(item.schedule_day));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));
    return map;
  }, [monthEntries]);

  // Quick-glance coverage summary shown at the top of the Daily Menu Grid -
  // which visible days still need attention, at a glance, before scrolling
  // through the whole table.
  const dailyGridSummary = useMemo(() => {
    let daysWithMenu = 0;
    let daysMissingCaterer = 0;
    visibleGridDays.forEach((date) => {
      const entries = entriesByDay[toDateKey(date)] || [];
      if (entries.length > 0) {
        daysWithMenu += 1;
        if (!entries.some((e) => e.cater_name1)) daysMissingCaterer += 1;
      }
    });
    return {
      totalDays: visibleGridDays.length,
      daysWithMenu,
      daysMissingMenu: visibleGridDays.length - daysWithMenu,
      daysMissingCaterer,
    };
  }, [visibleGridDays, entriesByDay]);

  // Every date always shows at least one row: every existing thaali_schedule
  // entry for that day, plus - now that the Caterer Grid's data lives here
  // too - a row for any day/type that has a real thaali_schedule_caterer
  // record but no thaali_schedule entry yet (a caterer set with no menu
  // picked out), plus (if there's neither) a single blank starter row, plus
  // any blank rows the user added via the "+" next to Date.
  const rowsForDay = (dateKey) => {
    const existing = (entriesByDay[dateKey] || []).map((entry) => ({ rowKey: `e${entry.id}`, entry }));
    const coveredTypes = new Set(existing.map(({ entry }) => entry.schedule_type));

    const catererOnly = (catererEntriesByDay[dateKey] || [])
      .filter((ce) => ce.caterer_id1 && !coveredTypes.has(ce.schedule_type))
      .map((ce) => ({ rowKey: `cat${ce.id}`, entry: null, catererOnlyEntry: ce }));

    const rows = [...existing, ...catererOnly];
    if (rows.length === 0) {
      rows.push({ rowKey: `new-${dateKey}`, entry: null });
    }
    (extraRows[dateKey] || []).forEach((rowKey) => rows.push({ rowKey, entry: null }));
    return rows;
  };

  const handleAddRow = (dateKey) => {
    nextLocalRowIdRef.current += 1;
    const rowKey = `new-${dateKey}-${nextLocalRowIdRef.current}`;
    setExtraRows((prev) => ({ ...prev, [dateKey]: [...(prev[dateKey] || []), rowKey] }));
  };

  const handleRemoveExtraRow = (dateKey, rowKey) => {
    setExtraRows((prev) => ({ ...prev, [dateKey]: (prev[dateKey] || []).filter((k) => k !== rowKey) }));
    setGridEdits((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  // Discards unsaved edits for a row, reverting it back to its last-saved
  // state (or blank, if it was never saved) - unlike handleRemoveExtraRow,
  // this doesn't delete the row itself, just the in-progress changes.
  const handleCancelRowEdits = (rowKey) => {
    setGridEdits((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  // defaultScheduleTypeValue only matters when entry is null - lets a
  // caterer-only row (see rowsForDay) default to the caterer record's own
  // type instead of always falling back to FMB.
  const initialRowFor = (entry, defaultScheduleTypeValue) => {
    if (!entry) {
      const scheduleType =
        SCHEDULE_TYPE_OPTIONS.find((o) => o.value === defaultScheduleTypeValue) || SCHEDULE_TYPE_OPTIONS[0];
      return {
        id: null,
        dishSelections: {},
        scheduleType,
        notes: "",
        notes2: "",
      };
    }
    const niyaazType = NIYAAZ_TYPE_BY_SCHEDULE[entry.schedule_type] ?? 0;
    const dishSelections = {};
    parseMenu(entry.menu).forEach((dishName) => {
      const dish = dishByNameAndType[niyaazType]?.[dishName?.trim().toLowerCase()];
      if (dish?.dish_type) {
        if (!dishSelections[dish.dish_type]) dishSelections[dish.dish_type] = [];
        dishSelections[dish.dish_type].push({ value: dish.id, label: dish.dish_name });
      }
    });
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === entry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: entry.id,
      dishSelections,
      scheduleType,
      notes: entry.notes || "",
      notes2: entry.notes2 || "",
    };
  };

  // Resolves the effective Lead Caterer/Team Member 1/2/status/record-id for
  // a row: a real thaali_schedule_caterer record for this day+(effective)
  // type wins; failing that, a legacy caterer set directly on
  // thaali_schedule (cater_name1, no caterer-table record at all) is
  // treated the same way the old Caterer Grid merge did - shown as already
  // Confirmed, since it's already live on the schedule.
  function resolveCatererDefaults(entry, dateKey, scheduleTypeValue) {
    const catererEntry = getCatererEntryForRow(dateKey, scheduleTypeValue);
    if (catererEntry) {
      return {
        leadCaterer: catererEntry.leadCaterer,
        teamMember1: catererEntry.teamMember1,
        teamMember2: catererEntry.teamMember2,
        status: catererEntry.status,
        catererRecordId: catererEntry.id,
      };
    }
    const legacyId = entry?.cater_name1 ? catererIdByName[entry.cater_name1.trim().toLowerCase()] : null;
    if (legacyId) {
      return {
        leadCaterer: catererOptionFor(legacyId),
        teamMember1: null,
        teamMember2: null,
        status: "CONFIRMED",
        catererRecordId: null,
      };
    }
    return { leadCaterer: null, teamMember1: null, teamMember2: null, status: null, catererRecordId: null };
  }

  const getEffectiveRow = (rowKey, entry, dateKey, catererOnlyEntry) => {
    const initial = initialRowFor(entry, catererOnlyEntry?.schedule_type);
    const edit = gridEdits[rowKey];
    const dishSelections = { ...initial.dishSelections, ...(edit?.dishSelections || {}) };
    // Every category always renders at least one (possibly empty) slot.
    categories.forEach((category) => {
      if (!dishSelections[category] || dishSelections[category].length === 0) {
        dishSelections[category] = [null];
      }
    });
    const scheduleType = edit && edit.scheduleType !== undefined ? edit.scheduleType : initial.scheduleType;
    const catererDefaults = resolveCatererDefaults(entry, dateKey, scheduleType?.value);
    return {
      id: initial.id,
      dishSelections,
      scheduleType,
      leadCaterer: edit && edit.leadCaterer !== undefined ? edit.leadCaterer : catererDefaults.leadCaterer,
      teamMember1: edit && edit.teamMember1 !== undefined ? edit.teamMember1 : catererDefaults.teamMember1,
      teamMember2: edit && edit.teamMember2 !== undefined ? edit.teamMember2 : catererDefaults.teamMember2,
      catererStatus: catererDefaults.status, // read-only display, not locally editable
      catererRecordId: catererDefaults.catererRecordId,
      notes: edit && edit.notes !== undefined ? edit.notes : initial.notes,
      notes2: edit && edit.notes2 !== undefined ? edit.notes2 : initial.notes2,
    };
  };

  const handleDishSelect = (rowKey, category, slotIndex, option, currentSlots) => {
    const nextSlots = [...currentSlots];
    nextSlots[slotIndex] = option;
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: nextSlots },
        },
      };
    });
  };

  const handleAddDishSlot = (rowKey, category, currentSlots) => {
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: [...currentSlots, null] },
        },
      };
    });
  };

  const handleRemoveDishSlot = (rowKey, category, slotIndex, currentSlots) => {
    if (currentSlots.length <= 1) return;
    const nextSlots = currentSlots.filter((_, i) => i !== slotIndex);
    setGridEdits((prev) => {
      const current = prev[rowKey] || {};
      return {
        ...prev,
        [rowKey]: {
          ...current,
          dishSelections: { ...(current.dishSelections || {}), [category]: nextSlots },
        },
      };
    });
  };

  const handleGridCatererFieldChange = (rowKey, field, option) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [field]: option },
    }));
  };

  const handleAddTeamMemberSlot = (rowKey) => {
    setTeamMemberSlotCounts((prev) => ({ ...prev, [rowKey]: Math.min(2, (prev[rowKey] || 0) + 1) }));
  };

  // Removing slot 0 (Team Member 1) shifts slot 1's value up rather than
  // just clearing it, same as dish slot removal (handleRemoveDishSlot) -
  // otherwise a filled Team Member 2 would be stranded behind a now-empty
  // Team Member 1 with no way to move it up.
  const handleRemoveTeamMemberSlot = (rowKey, slotIndex, row) => {
    if (slotIndex === 0) {
      handleGridCatererFieldChange(rowKey, "teamMember1", row.teamMember2 || null);
      handleGridCatererFieldChange(rowKey, "teamMember2", null);
    } else {
      handleGridCatererFieldChange(rowKey, "teamMember2", null);
    }
    setTeamMemberSlotCounts((prev) => {
      const current = prev[rowKey] || 0;
      if (current <= 0) return prev;
      return { ...prev, [rowKey]: current - 1 };
    });
  };

  // A row locked by a Confirmed status re-locks itself the next time it's
  // touched (schedule-type switch, or after a fresh save - see
  // handleSaveRow) - this only clears that lock for the CURRENT edit pass.
  const handleUnlockCatererFields = (rowKey) => {
    setUnlockedCatererRows((prev) => ({ ...prev, [rowKey]: true }));
  };

  // "Cancel" (the unlocked-state counterpart of "Edit") re-locks the row and
  // reverts ONLY its Lead Caterer/Team Member edits - any in-flight
  // dish/notes edits on the same row are left alone, unlike
  // handleCancelRowEdits which discards everything.
  const handleCancelCatererUnlock = (rowKey) => {
    setUnlockedCatererRows((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setTeamMemberSlotCounts((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setGridEdits((prev) => {
      const edit = prev[rowKey];
      if (!edit) return prev;
      const { leadCaterer, teamMember1, teamMember2, ...rest } = edit;
      const next = { ...prev };
      if (Object.keys(rest).length === 0) {
        delete next[rowKey];
      } else {
        next[rowKey] = rest;
      }
      return next;
    });
  };

  // Switching FMB <-> Miqaat changes which dish list is valid, so any
  // already-picked dishes (which may belong to the other niyaaz type) are
  // cleared and must be re-picked from the newly-filtered options. The
  // caterer fields are tied to day+type too (a different type means a
  // different thaali_schedule_caterer record), so they reset the same way.
  const handleScheduleTypeSelect = (rowKey, option) => {
    setUnlockedCatererRows((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    const blankDishSelections = {};
    categories.forEach((category) => {
      blankDishSelections[category] = [null];
    });
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        scheduleType: option,
        dishSelections: blankDishSelections,
        leadCaterer: null,
        teamMember1: null,
        teamMember2: null,
      },
    }));
  };

  const handleNotesChange = (rowKey, value) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), notes: value },
    }));
  };

  const handleNotes2Change = (rowKey, value) => {
    setGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), notes2: value },
    }));
  };

  // Saving a row now branches on what's actually filled in:
  //  - Lead Caterer only (no dishes): create/update thaali_schedule_caterer
  //    only, status PENDING - no thaali_schedule draft is created.
  //  - Lead Caterer + dishes: thaali_schedule_caterer is saved as CONFIRMED
  //    (a full caterer+menu combo is considered a complete assignment) AND
  //    the thaali_schedule draft is created/updated as before.
  //  - Dishes only, no caterer: unchanged - just the thaali_schedule draft.
  const handleSaveRow = (rowKey, dateKey, entry, catererOnlyEntry) => {
    const row = getEffectiveRow(rowKey, entry, dateKey, catererOnlyEntry);
    const dishArray = categories
      .flatMap((category) => row.dishSelections[category] || [])
      .filter(Boolean)
      .map((option) => ({ dish_id: option.value, quantity: "FULL", is_fateha: "false" }));

    const hasDishes = dishArray.length > 0;
    const hasCaterer = Boolean(row.leadCaterer);

    if (!hasDishes && !hasCaterer) {
      toast.warning("Pick a Lead Caterer or at least one dish before saving this row.");
      return;
    }

    const dayString = dateKey; // already YYYY-MM-DD
    setSavingRows((prev) => ({ ...prev, [rowKey]: true }));

    const requests = [];

    if (hasCaterer) {
      const catererRequestData = {
        schedule_day: dayString,
        schedule_type: row.scheduleType?.value || "FMBTHALI",
        caterer_id1: row.leadCaterer?.value,
        caterer_id2: row.teamMember1?.value,
        caterer_id3: row.teamMember2?.value,
        status: hasDishes ? "CONFIRMED" : "PENDING",
        is_active: "1",
        source: "community",
        created_user: access_id,
      };
      requests.push(
        row.catererRecordId
          ? dispatch(updateCatererSchedule({ id: row.catererRecordId, values: catererRequestData }))
          : dispatch(createCatererSchedule(catererRequestData))
      );
    }

    if (hasDishes) {
      // Whether any dish picked for the Rice/Roti column is actually a
      // rice/roti item by name (isRice/isRoti) - drives allow_skip_rice/
      // allow_skip_roti on save, since the dish master's own attribute for
      // this isn't reliably set.
      const riceAllowSkip = (row.dishSelections.Rice || []).some((slot) => slot?.label && isRice(slot.label.toLowerCase()));
      const rotiAllowSkip = (row.dishSelections.Roti || []).some((slot) => slot?.label && isRoti(slot.label.toLowerCase()));

      const requestData = {
        schedule_type: row.scheduleType?.value || "FMBTHALI",
        schedule_day: dayString,
        pickup_start_time: dayString,
        pickup_end_time: dayString,
        rsvp_date: dayString,
        rsvp_time: "10.00 AM",
        is_niyaaz: "0",
        jamanType: "COOKING",
        dish: dishArray,
        caterer_id1: row.leadCaterer?.value,
        notes: row.notes || "",
        notes2: row.notes2 || "",
        dish_id: "",
        order_by: "",
        source: "community",
        created_user: access_id,
        is_active: "1",
        portion_type_code: "1",
        pickup_location_code: "2",
        quantity: "HALF",
        fateha_name: "",
        allow_skip_rice: riceAllowSkip ? "1" : "0",
        allow_skip_roti: rotiAllowSkip ? "1" : "0",
        allow_skip_mithas: "0",
        save_draft: "1",
      };

      requests.push(
        row.id
          ? dispatch(Updatethali({ id: row.id, values: requestData })).then((res) => {
              if (res?.payload?.status === true) {
                toast.success(res?.payload?.message || "Draft saved");
              } else {
                toast.error(res?.payload?.message || "Failed to save draft");
              }
            })
          : dispatch(createCalender(requestData)) // createCalender already toasts success/failure itself
      );
    }

    Promise.all(requests)
      .then(() => Promise.all([refetchSchedule(), refetchCatererSchedule()]))
      .then(() => {
        setGridEdits((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
        // Once a locally-added blank row is actually saved, it becomes a
        // real backend entry (picked up via entriesByDay on the next
        // render) - drop its placeholder so it doesn't also linger as an
        // extra empty row next to the one that now holds its data.
        setExtraRows((prev) => {
          if (!prev[dateKey]?.includes(rowKey)) return prev;
          return { ...prev, [dateKey]: prev[dateKey].filter((k) => k !== rowKey) };
        });
        // A row saved with both caterer+dishes is now Confirmed - re-lock it
        // even if the user had unlocked it to make this edit.
        if (hasCaterer && hasDishes) {
          setUnlockedCatererRows((prev) => {
            if (!prev[rowKey]) return prev;
            const next = { ...prev };
            delete next[rowKey];
            return next;
          });
        }
      })
      .finally(() => {
        setSavingRows((prev) => ({ ...prev, [rowKey]: false }));
      });
  };

  // ---- Caterer Grid (thaali_schedule_caterer) ----

  const catererMonthEntries = useMemo(
    () => filterEntriesByRange(catererScheduleList, rangeStart, rangeEnd),
    [catererScheduleList, rangeStart, rangeEnd]
  );

  const catererEntriesByDay = useMemo(() => {
    const map = {};
    catererMonthEntries.forEach((item) => {
      if (!item?.schedule_day) return;
      const key = toDateKey(new Date(item.schedule_day));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0)));
    return map;
  }, [catererMonthEntries]);

  // Quick-glance coverage summary for the Caterer Grid - counts merged-in
  // days (a Daily Menu Grid caterer with no thaali_schedule_caterer record
  // yet) as Confirmed too, matching how they're actually displayed in the
  // grid below (see catererRowsForDay).
  const catererGridSummary = useMemo(() => {
    let daysWithCaterer = 0;
    let confirmedCount = 0;
    visibleGridDays.forEach((date) => {
      const dateKey = toDateKey(date);
      const realEntries = catererEntriesByDay[dateKey] || [];
      const thaliEntries = entriesByDay[dateKey] || [];
      const coveredTypes = new Set(realEntries.map((e) => e.schedule_type));
      const mergedCount = thaliEntries.filter((e) => e.cater_name1 && !coveredTypes.has(e.schedule_type)).length;

      const realWithCaterer = realEntries.filter((e) => e.caterer_id1);
      if (realWithCaterer.length > 0 || mergedCount > 0) daysWithCaterer += 1;
      confirmedCount += realWithCaterer.filter((e) => (e.status || "PENDING") === "CONFIRMED").length + mergedCount;
    });
    return {
      totalDays: visibleGridDays.length,
      daysWithCaterer,
      daysMissingCaterer: visibleGridDays.length - daysWithCaterer,
      confirmedCount,
    };
  }, [visibleGridDays, catererEntriesByDay, entriesByDay]);

  // Keyed by "dateKey|scheduleType" -> the caterer-schedule entry, used by
  // the Daily Menu Grid to pull in the Lead Caterer/Team Members/status for
  // that specific day+type (see getCatererEntryForRow below).
  const catererEntryByDayAndType = useMemo(() => {
    const map = {};
    catererScheduleList.forEach((item) => {
      if (!item?.schedule_day || !item?.schedule_type) return;
      const key = `${toDateKey(new Date(item.schedule_day))}|${item.schedule_type}`;
      // If duplicates exist for the same day+type, prefer the one with a
      // lead caterer actually set.
      if (!map[key] || (!map[key].caterer_id1 && item.caterer_id1)) {
        map[key] = item;
      }
    });
    return map;
  }, [catererScheduleList]);

  // Full thaali_schedule_caterer record (if any) for a given day+type,
  // resolved into Select-option shape - drives the Daily Menu Grid's Lead
  // Caterer/Team Member 1/2 fields and status badge.
  function getCatererEntryForRow(dateKey, scheduleTypeValue) {
    const entry = catererEntryByDayAndType[`${dateKey}|${scheduleTypeValue || "FMBTHALI"}`];
    if (!entry) return null;
    return {
      id: entry.id,
      leadCaterer: catererOptionFor(entry.caterer_id1),
      teamMember1: catererOptionFor(entry.caterer_id2),
      teamMember2: catererOptionFor(entry.caterer_id3),
      status: entry.status || null,
    };
  }

  // Days that already have a caterer set on the Daily Menu Grid (thaali_
  // schedule.cater_name1) but no thaali_schedule_caterer record yet for
  // that same day+type get merged in here as a virtual "Confirmed" row -
  // a caterer already being on the live schedule is treated as a done
  // assignment, not something still Pending. Merging is scoped per
  // schedule_type so a day that already has a real Caterer Grid row for
  // FMB, say, doesn't also swallow up a separate not-yet-tracked Miqaat
  // assignment on the same day.
  const catererRowsForDay = (dateKey) => {
    const existing = (catererEntriesByDay[dateKey] || []).map((entry) => ({ rowKey: `ce${entry.id}`, entry }));
    const coveredTypes = new Set(existing.map(({ entry }) => entry.schedule_type));

    const mergedFromThali = (entriesByDay[dateKey] || [])
      .filter((thaliEntry) => thaliEntry.cater_name1 && !coveredTypes.has(thaliEntry.schedule_type))
      .map((thaliEntry) => ({ rowKey: `cthali-${thaliEntry.id}`, entry: null, thaliEntry }));

    const rows = [...existing, ...mergedFromThali];
    if (rows.length === 0) {
      rows.push({ rowKey: `cnew-${dateKey}`, entry: null });
    }
    (extraCatererRows[dateKey] || []).forEach((rowKey) => rows.push({ rowKey, entry: null }));
    return rows;
  };

  const handleAddCatererRow = (dateKey) => {
    nextLocalCatererRowIdRef.current += 1;
    const rowKey = `cnew-${dateKey}-${nextLocalCatererRowIdRef.current}`;
    setExtraCatererRows((prev) => ({ ...prev, [dateKey]: [...(prev[dateKey] || []), rowKey] }));
  };

  const handleRemoveExtraCatererRow = (dateKey, rowKey) => {
    setExtraCatererRows((prev) => ({ ...prev, [dateKey]: (prev[dateKey] || []).filter((k) => k !== rowKey) }));
    setCatererGridEdits((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const handleCancelCatererRowEdits = (rowKey) => {
    setCatererGridEdits((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  function catererOptionFor(id) {
    if (!id) return null;
    const caterer = catererMaster.find((c) => c.id === id);
    if (!caterer) return null;
    return { value: caterer.id, label: `${caterer?.first_name || ""} ${caterer?.last_name || ""}`.trim() };
  }

  const initialCatererRowFor = (entry) => {
    if (!entry) {
      return {
        id: null,
        scheduleType: SCHEDULE_TYPE_OPTIONS[0],
        leadCaterer: null,
        teamMember1: null,
        teamMember2: null,
        notes: "",
        status: null,
      };
    }
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === entry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: entry.id,
      scheduleType,
      leadCaterer: catererOptionFor(entry.caterer_id1),
      teamMember1: catererOptionFor(entry.caterer_id2),
      teamMember2: catererOptionFor(entry.caterer_id3),
      notes: entry.notes || "",
      status: entry.status || null,
    };
  };

  // A day/type that already has a caterer set on the Daily Menu Grid but no
  // thaali_schedule_caterer record yet - built from the thaali_schedule
  // entry itself (cater_name1 is a name, not an id, so resolve it the same
  // way the Daily Menu Grid's own initialRowFor does). Treated as Confirmed
  // since the caterer is already live on the schedule; saving this row
  // creates a real thaali_schedule_caterer record (id stays null until then).
  const initialCatererRowFromThaliEntry = (thaliEntry) => {
    const catererId = catererIdByName[thaliEntry.cater_name1?.trim().toLowerCase()];
    const scheduleType =
      SCHEDULE_TYPE_OPTIONS.find((o) => o.value === thaliEntry.schedule_type) || SCHEDULE_TYPE_OPTIONS[0];
    return {
      id: null,
      scheduleType,
      leadCaterer: catererId ? catererOptionFor(catererId) : null,
      teamMember1: null,
      teamMember2: null,
      notes: "",
      status: "CONFIRMED",
    };
  };

  const getEffectiveCatererRow = (rowKey, entry, thaliEntry) => {
    const initial = entry
      ? initialCatererRowFor(entry)
      : thaliEntry
      ? initialCatererRowFromThaliEntry(thaliEntry)
      : initialCatererRowFor(null);
    const edit = catererGridEdits[rowKey];
    if (!edit) return initial;
    return {
      id: initial.id,
      scheduleType: edit.scheduleType !== undefined ? edit.scheduleType : initial.scheduleType,
      leadCaterer: edit.leadCaterer !== undefined ? edit.leadCaterer : initial.leadCaterer,
      teamMember1: edit.teamMember1 !== undefined ? edit.teamMember1 : initial.teamMember1,
      teamMember2: edit.teamMember2 !== undefined ? edit.teamMember2 : initial.teamMember2,
      notes: edit.notes !== undefined ? edit.notes : initial.notes,
      status: initial.status, // status is mobile-app-driven, never locally edited
    };
  };

  const handleCatererFieldChange = (rowKey, field, value) => {
    setCatererGridEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [field]: value },
    }));
  };

  const handleSaveCatererRow = (rowKey, dateKey, entry, thaliEntry) => {
    const row = getEffectiveCatererRow(rowKey, entry, thaliEntry);
    if (!row.leadCaterer) {
      toast.warning("Pick a Lead Caterer before saving this row.");
      return;
    }

    const requestData = {
      schedule_day: dateKey,
      schedule_type: row.scheduleType?.value || "FMBTHALI",
      caterer_id1: row.leadCaterer?.value,
      caterer_id2: row.teamMember1?.value,
      caterer_id3: row.teamMember2?.value,
      notes: row.notes || "",
      is_active: "1",
      source: "community",
      created_user: access_id,
    };
    // Status is intentionally omitted here - it's set by the caterer via
    // the mobile app, not from this admin screen. On first create it should
    // default server-side to "Pending".

    setSavingCatererRows((prev) => ({ ...prev, [rowKey]: true }));

    const request = row.id
      ? dispatch(updateCatererSchedule({ id: row.id, values: requestData })).then((res) => {
          if (res?.payload?.status === true) {
            toast.success(res?.payload?.message || "Caterer schedule saved");
          } else {
            toast.error(res?.payload?.message || "Failed to save caterer schedule");
          }
        })
      : dispatch(createCatererSchedule(requestData));

    request
      .then(() => refetchCatererSchedule())
      .then(() => {
        setCatererGridEdits((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
        setExtraCatererRows((prev) => {
          if (!prev[dateKey]?.includes(rowKey)) return prev;
          return { ...prev, [dateKey]: prev[dateKey].filter((k) => k !== rowKey) };
        });
      })
      .finally(() => {
        setSavingCatererRows((prev) => ({ ...prev, [rowKey]: false }));
      });
  };

  // Statistics view in quarterly mode steps by a full 3-month quarter per
  // click instead of one month; the Daily Menu Grid (and monthly stats)
  // always step by 1.
  const monthNavStep = viewMode === "stats" && statsRangeMode === "quarterly" ? 3 : 1;

  function shiftHijriMonth(year, month, delta) {
    const total = year * 12 + month + delta;
    return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
  }

  const handlePrevMonth = () => {
    const next = shiftHijriMonth(hijriYear, hijriMonth, -monthNavStep);
    setHijriYear(next.year);
    setHijriMonth(next.month);
  };

  const handleNextMonth = () => {
    const next = shiftHijriMonth(hijriYear, hijriMonth, monthNavStep);
    setHijriYear(next.year);
    setHijriMonth(next.month);
  };

  const statsQuarterEnd = shiftHijriMonth(hijriYear, hijriMonth, 2);

  // Coming from Statistics (which may be parked on a different month/quarter
  // via its own nav or quarterly mode), the Daily Menu Grid should always
  // open on the current Hijri month rather than wherever Statistics left off.
  const handleShowGrid = () => {
    if (viewMode === "stats") {
      setHijriYear(initialHijri.year);
      setHijriMonth(initialHijri.month);
    }
    setViewMode("grid");
  };

  const handleShowCatererGrid = () => {
    if (viewMode === "stats") {
      setHijriYear(initialHijri.year);
      setHijriMonth(initialHijri.month);
    }
    setViewMode("caterer");
  };

  return (
    <div>
      <Header />
      <div className="side_div_menu" style={{ display: "flex", height: "100%" }}>
        <Sidebarmenu />
        <section className="main_section">
          <div className="custam">
            <div className="main_section2">
              <div className="ExportInventory thali_section Thali_page">
                <div className="container-fluid">
                  <div className="thili_div">
                    <h1 className="title_heading">Menu Planning</h1>
                    <div className="mp-header-controls">
                      {viewMode === "stats" && (
                        <button
                          type="button"
                          className={`mp-stats-mode-switch ${statsRangeMode === "quarterly" ? "is-quarterly" : ""}`}
                          onClick={() => setStatsRangeMode((m) => (m === "monthly" ? "quarterly" : "monthly"))}
                          title="Toggle between monthly and quarterly statistics"
                        >
                          <span className="mp-stats-mode-option">Month</span>
                          <span className="mp-stats-mode-option">Quarter</span>
                          <span className="mp-stats-mode-thumb" />
                        </button>
                      )}
                      <div className="mp-month-nav">
                        <button className="btn thali_btn mp-month-btn" onClick={handlePrevMonth} title="Previous Hijri month">
                          &lsaquo;
                        </button>
                        <span className="mp-month-label">
                          {viewMode === "stats" && statsRangeMode === "quarterly"
                            ? `${HIJRI_MONTH_NAMES[hijriMonth]} ${hijriYear}H - ${HIJRI_MONTH_NAMES[statsQuarterEnd.month]} ${statsQuarterEnd.year}H`
                            : `${HIJRI_MONTH_NAMES[hijriMonth]} ${hijriYear}H`}
                        </span>
                        <button className="btn thali_btn mp-month-btn" onClick={handleNextMonth} title="Next Hijri month">
                          &rsaquo;
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mp-view-toggle">
                    {SHOW_CATERER_GRID_TOGGLE && (
                      <button
                        className={`btn thali_btn mp-toggle-btn ${viewMode === "caterer" ? "active" : ""}`}
                        onClick={handleShowCatererGrid}
                      >
                        Caterer Grid
                      </button>
                    )}
                    <button
                      className={`btn thali_btn mp-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                      onClick={handleShowGrid}
                    >
                      Daily Menu Grid
                    </button>
                    <button
                      className={`btn thali_btn mp-toggle-btn ${viewMode === "stats" ? "active" : ""}`}
                      onClick={() => setViewMode("stats")}
                    >
                      Statistics
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="Loading-img">
                    <img src={loadergif} alt="Loading" />
                  </div>
                ) : viewMode === "grid" ? (
                  <div className="container-fluid mp-grid-view-container">
                    <div className="mp-grid-summary-row">
                      <div className="mp-grid-summary-tile">
                        <span className="mp-grid-summary-value">{dailyGridSummary.totalDays}</span>
                        <span className="mp-grid-summary-label">Days Shown</span>
                      </div>
                      <div className="mp-grid-summary-tile">
                        <span className="mp-grid-summary-value">{dailyGridSummary.daysWithMenu}</span>
                        <span className="mp-grid-summary-label">Menu Set</span>
                      </div>
                      <div className="mp-grid-summary-tile mp-grid-summary-tile-warn">
                        <span className="mp-grid-summary-value">{dailyGridSummary.daysMissingMenu}</span>
                        <span className="mp-grid-summary-label">Menu Missing</span>
                      </div>
                      <div className="mp-grid-summary-tile mp-grid-summary-tile-warn">
                        <span className="mp-grid-summary-value">{dailyGridSummary.daysMissingCaterer}</span>
                        <span className="mp-grid-summary-label">Caterer Missing</span>
                      </div>
                    </div>
                    <div className="mp-grid-toolbar">
                      <p className="mp-empty-note mp-grid-hint">
                        Pick a dish per category (and a caterer), then hit the save icon for that row.
                        Use the + next to a dish list to add another dish of that type, or + next to Date to add a
                        whole second menu for that day. Saved rows show up as drafts on the Calendar Setup page for
                        final review and publish.
                      </p>
                      <div className="mp-grid-actions">
                        {isCurrentMonth && (
                          <button
                            type="button"
                            className="btn thali_btn mp-toggle-btn mp-collapse-past-btn"
                            onClick={() => setPastDaysCollapsed((v) => !v)}
                          >
                            {pastDaysCollapsed ? "Show Past Days" : "Hide Past Days"}
                          </button>
                        )}
                        <div className="mp-dish-search" title="Look up which dish type and niyaaz a dish belongs to">
                          <Select
                            classNamePrefix="mp-dish-search-select"
                            isClearable
                            placeholder="Search dish name..."
                            options={dishSearchOptions}
                            value={dishSearchValue}
                            onChange={setDishSearchValue}
                            formatOptionLabel={formatDishSearchOption}
                            noOptionsMessage={() => "No dish found"}
                            {...selectMenuPortalProps}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mp-grid-scroll" ref={gridScrollRef}>
                      <table className="mp-grid-table">
                        <thead>
                          <tr>
                            <th className="mp-grid-save-icon-col"></th>
                            <th className="mp-grid-date-col">Date</th>
                            <th className="mp-grid-type-col">Type</th>
                            <th className="mp-grid-caterer-cell">Caterer</th>
                            {categories.map((category) => (
                              <th key={category}>{category}</th>
                            ))}
                            <th>Notes</th>
                            <th>Temp Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleGridDays.map((date) => {
                            const dateKey = toDateKey(date);
                            const hijri = getHijriParts(date);
                            const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
                            const isToday = dateKey === todayKey;
                            const isPast = dateKey < todayKey;
                            const isSunday = date.getDay() === 0;
                            const rows = rowsForDay(dateKey);

                            return rows.map(({ rowKey, entry, catererOnlyEntry }, rowIndex) => {
                              const row = getEffectiveRow(rowKey, entry, dateKey, catererOnlyEntry);
                              const isDirty = Boolean(gridEdits[rowKey]);
                              const isSaved = Boolean(row.id) && !isDirty;
                              const isMiqaat = row.scheduleType?.value === "MIQAAT";
                              const isRemovableExtra = !entry && (extraRows[dateKey] || []).includes(rowKey);
                              // Once a caterer assignment is Confirmed, its fields lock by
                              // default - the edit icon (see the Caterer cell below) unlocks
                              // them for the current pass; re-locks after the next save.
                              const isCatererLocked = row.catererStatus === "CONFIRMED" && !unlockedCatererRows[rowKey];
                              const teamMemberVisibleCount = Math.max(
                                teamMemberSlotCounts[rowKey] || 0,
                                row.teamMember1 ? 1 : 0,
                                row.teamMember2 ? 2 : 0
                              );
                              const rowClass = [
                                isSunday ? "mp-grid-row-sunday" : "",
                                isDirty ? "mp-grid-row-dirty" : "",
                                isToday ? "mp-grid-row-today" : "",
                                isPast ? "mp-grid-row-past" : "",
                              ].filter(Boolean).join(" ");

                              return (
                                <tr key={rowKey} className={rowClass}>
                                  <td className="mp-grid-save-icon-col">
                                    <div className="mp-grid-save-icon-wrap">
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-save-icon-btn"
                                          title={isSaved ? "Saved" : "Save Draft"}
                                          disabled={!isDirty || savingRows[rowKey]}
                                          onClick={() => handleSaveRow(rowKey, dateKey, entry, catererOnlyEntry)}
                                        >
                                          <Save fontSize="small" />
                                          {isSaved && <CheckCircle className="mp-grid-save-tick" fontSize="small" />}
                                        </button>
                                      )}
                                      {!isPast && isDirty && (
                                        <button
                                          type="button"
                                          className="mp-grid-cancel-icon-btn"
                                          title="Discard changes"
                                          onClick={() => handleCancelRowEdits(rowKey)}
                                        >
                                          <Undo fontSize="small" />
                                        </button>
                                      )}
                                      {!isPast && isRemovableExtra && (
                                        <button
                                          type="button"
                                          className="mp-grid-remove-row-btn"
                                          title="Remove this row"
                                          onClick={() => handleRemoveExtraRow(dateKey, rowKey)}
                                        >
                                          <Close fontSize="inherit" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-date-col" rowSpan={rows.length}>
                                      <div className="mp-grid-date-greg">
                                        {weekday}, {formatDate(date)}
                                      </div>
                                      <div className="mp-grid-date-hijri">{hijri.day} {hijri.month}</div>
                                      {isToday && <span className="mp-today-tag mp-today-tag-below">Today</span>}
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-add-row-btn"
                                          title="Add another menu for this day"
                                          onClick={() => handleAddRow(dateKey)}
                                        >
                                          <AddCircleOutline fontSize="inherit" /> Add Menu
                                        </button>
                                      )}
                                    </td>
                                  )}
                                  <td className="mp-grid-type-col">
                                    <Select
                                      classNamePrefix="mp-select"
                                      isDisabled={isPast}
                                      options={SCHEDULE_TYPE_OPTIONS}
                                      value={row.scheduleType}
                                      onChange={(option) => handleScheduleTypeSelect(rowKey, option)}
                                      {...selectMenuPortalProps}
                                    />
                                    {isMiqaat && <span className="mp-miqaat-tag">Miqaat</span>}
                                  </td>
                                  <td className="mp-grid-cell mp-grid-caterer-cell">
                                    <div className="mp-grid-lead-caterer-select" title={row.leadCaterer?.label || ""}>
                                      <Select
                                        classNamePrefix="mp-select"
                                        className="mp-grid-caterer-select"
                                        isClearable={!isCatererLocked}
                                        isDisabled={isPast || isCatererLocked}
                                        placeholder="Lead Caterer"
                                        options={getCatererOptions(dateKey)}
                                        value={row.leadCaterer}
                                        onChange={(option) => handleGridCatererFieldChange(rowKey, "leadCaterer", option)}
                                        formatOptionLabel={formatOptionLabelWithCounts}
                                        {...selectMenuPortalProps}
                                      />
                                      {row.catererStatus && (
                                        <span
                                          className={`mp-caterer-status-icon mp-caterer-status-icon-${row.catererStatus.toLowerCase()}`}
                                          title={CATERER_STATUS_LABELS[row.catererStatus] || CATERER_STATUS_LABELS.PENDING}
                                        >
                                          {row.catererStatus === "CONFIRMED" ? (
                                            <CheckCircleOutline fontSize="inherit" />
                                          ) : row.catererStatus === "TENTATIVE" ? (
                                            <Schedule fontSize="inherit" />
                                          ) : (
                                            <HourglassEmpty fontSize="inherit" />
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {!isPast && row.catererStatus === "CONFIRMED" && (
                                      isCatererLocked ? (
                                        <button
                                          type="button"
                                          className="mp-grid-add-dish-btn"
                                          title="Unlock to edit"
                                          onClick={() => handleUnlockCatererFields(rowKey)}
                                        >
                                          <Edit fontSize="inherit" /> Edit
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="mp-grid-add-dish-btn mp-grid-caterer-cancel-btn"
                                          title="Discard these changes and re-lock"
                                          onClick={() => handleCancelCatererUnlock(rowKey)}
                                        >
                                          <Close fontSize="inherit" /> Cancel
                                        </button>
                                      )
                                    )}
                                    {Array.from({ length: teamMemberVisibleCount }).map((_, slotIdx) => {
                                      const field = slotIdx === 0 ? "teamMember1" : "teamMember2";
                                      return (
                                        <div className="mp-grid-team-member-row" key={field} title={row[field]?.label || ""}>
                                          <Select
                                            classNamePrefix="mp-select"
                                            className="mp-grid-caterer-select mp-grid-team-member-select"
                                            isClearable
                                            isDisabled={isPast || isCatererLocked}
                                            placeholder={`Team Member ${slotIdx + 1}`}
                                            options={getCatererOptions(dateKey)}
                                            value={row[field]}
                                            onChange={(option) => handleGridCatererFieldChange(rowKey, field, option)}
                                            formatOptionLabel={formatOptionLabelWithCounts}
                                            {...selectMenuPortalProps}
                                          />
                                          {!isPast && !isCatererLocked && (
                                            <button
                                              type="button"
                                              className="mp-grid-dish-remove-btn"
                                              title="Remove"
                                              onClick={() => handleRemoveTeamMemberSlot(rowKey, slotIdx, row)}
                                            >
                                              <Close fontSize="inherit" />
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {!isPast && !isCatererLocked && teamMemberVisibleCount < 2 && (
                                      <button
                                        type="button"
                                        className="mp-grid-add-dish-btn"
                                        title="Add Team Member"
                                        onClick={() => handleAddTeamMemberSlot(rowKey)}
                                      >
                                        <AddCircleOutline fontSize="inherit" /> Add Team Member
                                      </button>
                                    )}
                                  </td>
                                  {categories.map((category) => {
                                    const slots = row.dishSelections[category] || [null];
                                    const options = getDishOptions(category, row.scheduleType?.value, dateKey);
                                    return (
                                      <td key={category} className="mp-grid-cell mp-grid-dish-cell">
                                        {slots.map((slot, slotIndex) => (
                                          <div className="mp-grid-dish-slot" key={slotIndex} title={slot?.label || ""}>
                                            <Select
                                              classNamePrefix="mp-select"
                                              className="mp-grid-dish-slot-select"
                                              isClearable
                                              isDisabled={isPast}
                                              placeholder="--"
                                              options={options}
                                              value={slot}
                                              onChange={(option) => handleDishSelect(rowKey, category, slotIndex, option, slots)}
                                              components={{ ValueContainer: DishTypeValueContainer }}
                                              dishTypeLabel={category}
                                              formatOptionLabel={formatOptionLabelWithCounts}
                                              {...selectMenuPortalProps}
                                            />
                                            {!isPast && slots.length > 1 && (
                                              <button
                                                type="button"
                                                className="mp-grid-dish-remove-btn"
                                                title="Remove"
                                                onClick={() => handleRemoveDishSlot(rowKey, category, slotIndex, slots)}
                                              >
                                                <Close fontSize="inherit" />
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        {!isPast && (
                                          <button
                                            type="button"
                                            className="mp-grid-add-dish-btn"
                                            title={`Add another ${category} dish`}
                                            onClick={() => handleAddDishSlot(rowKey, category, slots)}
                                          >
                                            <AddCircleOutline fontSize="inherit" /> Add
                                          </button>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes || ""}
                                      disabled={isPast}
                                      placeholder="Notes..."
                                      value={row.notes || ""}
                                      onChange={(e) => handleNotesChange(rowKey, e.target.value)}
                                    />
                                  </td>
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes2 || ""}
                                      disabled={isPast}
                                      placeholder="Temp Notes..."
                                      value={row.notes2 || ""}
                                      onChange={(e) => handleNotes2Change(rowKey, e.target.value)}
                                    />
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mp-grid-custom-scrollbar" ref={gridScrollBarRef} onMouseDown={handleScrollTrackMouseDown}>
                      <div
                        className="mp-grid-custom-scrollbar-thumb"
                        ref={gridScrollThumbRef}
                        onMouseDown={handleScrollThumbMouseDown}
                      />
                    </div>
                  </div>
                ) : viewMode === "caterer" ? (
                  <div className="container-fluid mp-grid-view-container">
                    <div className="mp-grid-summary-row">
                      <div className="mp-grid-summary-tile">
                        <span className="mp-grid-summary-value">{catererGridSummary.totalDays}</span>
                        <span className="mp-grid-summary-label">Days Shown</span>
                      </div>
                      <div className="mp-grid-summary-tile">
                        <span className="mp-grid-summary-value">{catererGridSummary.daysWithCaterer}</span>
                        <span className="mp-grid-summary-label">Caterer Assigned</span>
                      </div>
                      <div className="mp-grid-summary-tile mp-grid-summary-tile-warn">
                        <span className="mp-grid-summary-value">{catererGridSummary.daysMissingCaterer}</span>
                        <span className="mp-grid-summary-label">Caterer Missing</span>
                      </div>
                      <div className="mp-grid-summary-tile mp-grid-summary-tile-good">
                        <span className="mp-grid-summary-value">{catererGridSummary.confirmedCount}</span>
                        <span className="mp-grid-summary-label">Confirmed</span>
                      </div>
                    </div>
                    <p className="mp-empty-note mp-grid-hint">
                      Assign the Lead Caterer (and up to 2 team members) per day. Once a Lead Caterer is set here,
                      the Daily Menu Grid's Caterer column for that day/type is prefilled and locked to it. Status is
                      set by the caterer from the mobile app and shown here read-only.
                    </p>
                    <div className="mp-grid-scroll" ref={gridScrollRef}>
                      <table className="mp-grid-table mp-caterer-grid-table">
                        <thead>
                          <tr>
                            <th className="mp-grid-save-icon-col"></th>
                            <th className="mp-grid-type-col">Type</th>
                            <th className="mp-grid-date-col">Date</th>
                            <th>Lead Caterer</th>
                            <th>Team Member 1</th>
                            <th>Team Member 2</th>
                            <th>Notes</th>
                            <th className="mp-grid-status-cell">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleGridDays.map((date) => {
                            const dateKey = toDateKey(date);
                            const hijri = getHijriParts(date);
                            const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
                            const isToday = dateKey === todayKey;
                            const isPast = dateKey < todayKey;
                            const isSunday = date.getDay() === 0;
                            const rows = catererRowsForDay(dateKey);

                            return rows.map(({ rowKey, entry, thaliEntry }, rowIndex) => {
                              const row = getEffectiveCatererRow(rowKey, entry, thaliEntry);
                              const isDirty = Boolean(catererGridEdits[rowKey]);
                              const isSaved = Boolean(row.id) && !isDirty;
                              const isMiqaat = row.scheduleType?.value === "MIQAAT";
                              const isRemovableExtra = !entry && (extraCatererRows[dateKey] || []).includes(rowKey);
                              const rowClass = [
                                isSunday ? "mp-grid-row-sunday" : "",
                                isDirty ? "mp-grid-row-dirty" : "",
                                isToday ? "mp-grid-row-today" : "",
                                isPast ? "mp-grid-row-past" : "",
                              ].filter(Boolean).join(" ");

                              return (
                                <tr key={rowKey} className={rowClass}>
                                  <td className="mp-grid-save-icon-col">
                                    <div className="mp-grid-save-icon-wrap">
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-save-icon-btn"
                                          title={isSaved ? "Saved" : "Save Draft"}
                                          disabled={!isDirty || savingCatererRows[rowKey]}
                                          onClick={() => handleSaveCatererRow(rowKey, dateKey, entry, thaliEntry)}
                                        >
                                          <Save fontSize="small" />
                                          {isSaved && <CheckCircle className="mp-grid-save-tick" fontSize="small" />}
                                        </button>
                                      )}
                                      {!isPast && isDirty && (
                                        <button
                                          type="button"
                                          className="mp-grid-cancel-icon-btn"
                                          title="Discard changes"
                                          onClick={() => handleCancelCatererRowEdits(rowKey)}
                                        >
                                          <Undo fontSize="small" />
                                        </button>
                                      )}
                                      {!isPast && isRemovableExtra && (
                                        <button
                                          type="button"
                                          className="mp-grid-remove-row-btn"
                                          title="Remove this row"
                                          onClick={() => handleRemoveExtraCatererRow(dateKey, rowKey)}
                                        >
                                          <Close fontSize="inherit" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="mp-grid-type-col">
                                    <Select
                                      classNamePrefix="mp-select"
                                      isDisabled={isPast}
                                      options={SCHEDULE_TYPE_OPTIONS}
                                      value={row.scheduleType}
                                      onChange={(option) => handleCatererFieldChange(rowKey, "scheduleType", option)}
                                      {...selectMenuPortalProps}
                                    />
                                    {isMiqaat && <span className="mp-miqaat-tag">Miqaat</span>}
                                  </td>
                                  {rowIndex === 0 && (
                                    <td className="mp-grid-date-col" rowSpan={rows.length}>
                                      <div className="mp-grid-date-greg">
                                        {weekday}, {formatDate(date)}
                                        {isToday && <span className="mp-today-tag">Today</span>}
                                      </div>
                                      <div className="mp-grid-date-hijri">{hijri.day} {hijri.month}</div>
                                      {!isPast && (
                                        <button
                                          type="button"
                                          className="mp-grid-add-row-btn"
                                          title="Add another caterer assignment for this day"
                                          onClick={() => handleAddCatererRow(dateKey)}
                                        >
                                          <AddCircleOutline fontSize="inherit" /> Add
                                        </button>
                                      )}
                                    </td>
                                  )}
                                  <td className="mp-grid-cell mp-grid-caterer-cell mp-grid-lead-caterer-cell">
                                    <Select
                                      classNamePrefix="mp-select"
                                      className="mp-grid-caterer-select"
                                      isClearable
                                      isDisabled={isPast}
                                      placeholder="Lead Caterer"
                                      options={getCatererOptions(dateKey)}
                                      value={row.leadCaterer}
                                      onChange={(option) => handleCatererFieldChange(rowKey, "leadCaterer", option)}
                                      formatOptionLabel={formatOptionLabelWithCounts}
                                      {...selectMenuPortalProps}
                                    />
                                  </td>
                                  <td className="mp-grid-cell mp-grid-caterer-cell">
                                    <Select
                                      classNamePrefix="mp-select"
                                      className="mp-grid-caterer-select"
                                      isClearable
                                      isDisabled={isPast}
                                      placeholder="Team Member 1"
                                      options={getCatererOptions(dateKey)}
                                      value={row.teamMember1}
                                      onChange={(option) => handleCatererFieldChange(rowKey, "teamMember1", option)}
                                      formatOptionLabel={formatOptionLabelWithCounts}
                                      {...selectMenuPortalProps}
                                    />
                                  </td>
                                  <td className="mp-grid-cell mp-grid-caterer-cell">
                                    <Select
                                      classNamePrefix="mp-select"
                                      className="mp-grid-caterer-select"
                                      isClearable
                                      isDisabled={isPast}
                                      placeholder="Team Member 2"
                                      options={getCatererOptions(dateKey)}
                                      value={row.teamMember2}
                                      onChange={(option) => handleCatererFieldChange(rowKey, "teamMember2", option)}
                                      formatOptionLabel={formatOptionLabelWithCounts}
                                      {...selectMenuPortalProps}
                                    />
                                  </td>
                                  <td className="mp-grid-cell">
                                    <textarea
                                      className="mp-grid-notes-input"
                                      title={row.notes || ""}
                                      disabled={isPast}
                                      placeholder="Notes..."
                                      value={row.notes || ""}
                                      onChange={(e) => handleCatererFieldChange(rowKey, "notes", e.target.value)}
                                    />
                                  </td>
                                  <td className="mp-grid-status-cell">
                                    {row.leadCaterer && (
                                      <span
                                        className={`mp-caterer-status-badge mp-caterer-status-${(row.status || "PENDING").toLowerCase()}`}
                                      >
                                        {CATERER_STATUS_LABELS[row.status] || CATERER_STATUS_LABELS.PENDING}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mp-grid-custom-scrollbar" ref={gridScrollBarRef} onMouseDown={handleScrollTrackMouseDown}>
                      <div
                        className="mp-grid-custom-scrollbar-thumb"
                        ref={gridScrollThumbRef}
                        onMouseDown={handleScrollThumbMouseDown}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="container-fluid">
                    {/* Summary (Statistics sheet equivalent) */}
                    <div className="row mt-2 mb-4">
                      <div className="col-lg-3 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Gregorian Range</h4>
                            <h1 className="mp-stat-range">
                              {formatDate(statsRangeStart)} - {formatDate(statsRangeEnd)}
                            </h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="Week_list INGREDIENTS_Cout">
                          <div className="week_text">
                            <h4>Scheduled Days</h4>
                            <h1>{summary.uniqueDays}</h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="Week_list">
                          <div className="week_text">
                            <h4>Thaali Entries</h4>
                            <h1>{summary.thaaliEntries}</h1>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="Week_list Thali_Count">
                          <div className="week_text">
                            <h4>Miqaat / Niyaz Entries</h4>
                            <h1>{summary.miqaatEntries}</h1>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Item Frequency */}
                    <h1 className="title_heading mp-section-heading">Item Frequency</h1>
                    {itemFrequency.length === 0 ? (
                      <p className="mp-empty-note">No dishes scheduled in this month.</p>
                    ) : (
                      <div className="mp-category-grid">
                        {itemFrequency.map(({ category, dishes }) => (
                          <div className="mp-category-card" key={category}>
                            <h5 className="thali-heding mp-category-title">{category}</h5>
                            <ul className="mp-dish-list">
                              {dishes.map((dish) => (
                                <li key={dish.name} className="mp-dish-row">
                                  <span className="mp-dish-name">{dish.name}</span>
                                  <span className="mp-count-badge">{dish.count}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Caterer Frequency */}
                    <h1 className="title_heading mp-section-heading">Caterer Frequency</h1>
                    {catererFrequency.length === 0 ? (
                      <p className="mp-empty-note">No caterer data available.</p>
                    ) : (
                      <div className="mp-category-card mp-caterer-card">
                        <ul className="mp-dish-list">
                          {catererFrequency.map((caterer) => (
                            <li
                              key={caterer.name}
                              className={`mp-dish-row ${caterer.count === 0 ? "mp-zero-count" : ""}`}
                            >
                              <span className="mp-dish-name">{caterer.name}</span>
                              <span className="mp-count-badge">{caterer.count}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
